<template lang="pug">
  //- именно label, а не button с input внутри: у наложенного input[type=file]
  //- курсор задаёт его шадоу-кнопка ::-webkit-file-upload-button, а не он сам
  label.b-crm-file-upload-button(:title="title" :class="{ disabled }")
    Icon(name="plus" pointer colored)
    span._label(v-if="label") {{ label }}
    input(type="file" hidden multiple :disabled="disabled" @change="pickFiles")
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

import popupModel from '@/model/popup';

import fileApi from '@/api/file';

/**
 * Кнопка «прикрепить файл» вместе со всей механикой загрузки: заглушка на
 * время отправки, прогресс, отмена и разбор неудачи.
 *
 * Рисует только кнопку. Список вложений остаётся за вызывающим — у формы
 * комментария он стоит над строкой ввода, а на деталке заявки отдельным
 * блоком, и общего компонента с фиксированной раскладкой им обоим не хватило
 * бы. Взамен компонент отдаёт наружу обе половины списка: уже загруженные
 * файлы событием change (полным новым набором), а те, что ещё летят, —
 * событием uploadingChange, в готовом для FileList виде.
 *
 * Летящие файлы компонент держит у себя, а не читает из пропа: за выбор можно
 * отдать сразу несколько файлов, и все они грузятся параллельно. Проп к этому
 * моменту ещё хранит значение до начала загрузки, так что вторая загрузка
 * затирала бы первую — из трёх выбранных файлов доезжал один.
 */
export default {
  name: 'CrmFileUploadButton',
  props: {
    // загруженные файлы: объекты ответа сервера, у каждого есть @id
    files: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
    title: {
      type: String,
      default: 'Прикрепить файл',
    },
    // подпись рядом с плюсом; без неё кнопка состоит из одной иконки
    label: String,
  },
  data() {
    return {
      // элементы в формате FileList, которые ещё летят на сервер:
      // { id, file, process, progress, removable, controller }
      uploading: [],
    };
  },
  methods: {
    /**
     * Вся выбранная пачка грузится параллельно, а наружу уходит одним
     * событием: по событию на файл каждое следующее считало бы files из пропа,
     * не успевшего обновиться после предыдущего, и перезаписывало бы его.
     */
    async pickFiles(event) {
      const items = [...event.target.files].map(file => this.createItem(file));
      // input сбрасываем сразу, иначе повторный выбор того же файла
      // не вызовет change
      event.target.value = '';
      if (!items.length) return;

      this.setUploading([...this.uploading, ...items]);

      const results = await Promise.all(items.map(item => this.uploadFile(item)));
      const uploaded = results.filter(Boolean);

      // Снимаем заглушки и отдаём результат одним махом: между этими двумя
      // строками файла нет ни в одном из списков, и отрисуйся кадр посередине
      // — он бы мигнул. Подряд Vue сведёт их в одну перерисовку
      this.uploading = this.uploading.filter(item => !items.includes(item));
      if (uploaded.length) this.$emit('change', [...this.files, ...uploaded]);
      this.emitUploading();
    },

    createItem(file) {
      return {
        id: uuidv4(),
        file,
        progress: 0,
        process: 'waiting',
        removable: true,
        controller: new AbortController(),
      };
    },

    /** Отдаёт загруженный файл или null, если не вышло. */
    async uploadFile(item) {
      const onProgress = (progress) => {
        item.progress = progress; // eslint-disable-line no-param-reassign
        item.process = progress > 0 ? 'uploading' : 'waiting'; // eslint-disable-line no-param-reassign
      };

      try {
        const { data } = await fileApi.create(item.file, onProgress, item.controller);
        return data;
      } catch (e) {
        // отмену загрузки инициировал сам пользователь (см. removeItem) —
        // сообщать не о чем; про настоящую неудачу иначе никто не узнал бы:
        // файл просто исчезал бы из формы
        if (!item.controller.signal.aborted) {
          await popupModel.addAlert('Не удалось загрузить файл', item.file.name);
        }
        return null;
      }
    },

    /**
     * Снять вложение: недогруженное отменяем, загруженное убираем из набора.
     * Зовёт вызывающий из обработчика remove у своего FileList — элементы
     * туда попадают и отсюда (uploading), и из его собственного списка.
     */
    removeItem(item) {
      if (item.controller) {
        item.controller.abort();
        this.setUploading(this.uploading.filter(i => i !== item));
        return;
      }

      this.$emit('change', this.files.filter(file => file !== item.file));
    },

    setUploading(items) {
      this.uploading = items;
      this.emitUploading();
    },

    emitUploading() {
      this.$emit('uploadingChange', this.uploading);
    },
  },
};
</script>

<style scoped lang="scss">
.crm-file-upload-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 24px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  color: var(--color-light-dark);

  &.disabled {
    opacity: 0.4;
    cursor: default;

    // Icon с pointer ставит cursor: pointer инлайном — перебиваем его
    // на выключенной кнопке, отдавая курсор самой кнопке
    ::v-deep * {
      pointer-events: none;
    }
  }
}
</style>
